import React from 'react';
import { useNavigate } from 'react-router-dom';
import CardMedia from './CardMedia';
import CardContent from './CardContent';
import CardMenu from './CardMenu';
import DeleteDialog from './DeleteDialog';
import CardActionBar from './CardActionBar';
import { School as GradeLevelIcon } from '@mui/icons-material';
import { useCardState } from '../../hooks/useCardState';
import StyledCard from '../../../../components/card/StyledCard';
import CardMeta from '../../../../components/card/CardMeta';

const ContentCard = ({
  id,
  thumbnailImageURL,
  title,
  category,
  synopsis,
  onRefresh,
  gradeLevel,
  // Replace fromPublishedTab boolean with more flexible tabType string
  tabType = 'draft' // 'draft', 'published', or 'archived'
}) => {
  const navigate = useNavigate();
  const {
    menuAnchor,
    deleteDialogOpen,
    handleMenuOpen,
    handleMenuClose,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm
  } = useCardState(id, onRefresh);

  // Determine if edit/action features should be available based on tab type
  const isEditable = tabType === 'draft';
  
  // Different background color or styling could be applied based on tabType
  const getCardStyle = () => {
    switch (tabType) {
      case 'published':
        return {}; // Default styling
      case 'archived':
        return { opacity: 0.8 }; // Slightly faded appearance for archived items
      default: // draft
        return {};
    }
  };

  const handleCardClick = () => {
    navigate(`/reading-practices/${tabType}/${id}`);
  };

  const handleEdit = (event) => {
    event.stopPropagation();
    handleMenuClose();
    navigate(`/reading-practices/${tabType}/${id}/edit`);
  };

  // Handle publish, archive, and restore (not implemented yet)
  const handlePublish = () => {
    // Future implementation
    // console.log('Publish content:', id);
    handleMenuClose();
  };

  const handleArchive = () => {
    // Future implementation
    // console.log('Archive content:', id);
    handleMenuClose();
  };

  const handleUnarchive = () => {
    // Future implementation
    // console.log('Restore content:', id);
    handleMenuClose();
  };

  // Determine appropriate actions based on tab type
  const getAvailableActions = () => {
    const actions = {
      view: true, // Always available
      edit: isEditable,
      delete: isEditable,
      // Could add more actions like 'restore' for archived items
      restore: tabType === 'archived',
      archive: tabType === 'published',
      publish: tabType === 'draft'
    };
    
    return actions;
  };

  const actions = getAvailableActions();

  return (
    <>
      <StyledCard onClick={handleCardClick} sx={getCardStyle()}>
        <CardMedia
          banner={thumbnailImageURL}
          title={title}
          badge={category}
        />
        
        <CardContent
          title={title}
          description={synopsis}
        >
          {gradeLevel && <CardMeta 
            info={`Grade ${gradeLevel}`} 
            icon={<GradeLevelIcon
              fontSize="small"
              sx={{ mr: 0.5, opacity: 0.7, color: 'text.secondary' }}
            />} 
          />}
        </CardContent>
        
        <CardActionBar
          onEditClick={handleEdit}
          onMenuOpen={handleMenuOpen}
          tabType={tabType}
        />
      </StyledCard>

      <CardMenu
        menuAnchor={menuAnchor}
        onClose={handleMenuClose}
        onViewClick={handleCardClick}
        onEditClick={handleEdit}
        onDeleteClick={handleDeleteClick}
        onPublishClick={handlePublish}
        onArchiveClick={handleArchive}
        onUnarchiveClick={handleUnarchive}
        tabType={tabType}
        actions={actions}
      />

      <DeleteDialog
        open={deleteDialogOpen}
        title={title}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default ContentCard;